import { useEffect, useMemo } from 'react'
import Footer from '@app/components/Footer/Footer'
import Navbar from '@app/components/Navbar/Navbar'
import Faq from '@app/components/Faq'
import Banner from '@app/components/Banner'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import { FormattedMessage, useIntl } from 'react-intl'
import { QuizStep, clearQuiz, updateQuiz } from '@app/store/slices/quiz'
import urls from '@app/routes/urls'
import { Navigate, useParams } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import S from './OrderConfirmation.styles'

interface IStep {
  id: number
  content: string
  footnote?: string
}

const OrderConfirmation = () => {
  const { messages } = useIntl()
  const p = useParams<{ id: string }>()
  const id = p.id as string

  const { records } = useAppSelector(state => state.orders)
  const dispatch = useAppDispatch()

  // Clear the quiz data when the component mounts
  useEffect(() => {
    dispatch(clearQuiz())
    // Reset the quiz step to the customer step
    dispatch(updateQuiz({ step: QuizStep.CUSTOMER, prevStep: QuizStep.NONE }))
  }, [dispatch])

  const stepList = useMemo(() => {
    const steps: Record<string, IStep> = {}
    const keys = Object.keys(messages).filter(o => o.startsWith('order_confirmation_page.content.step_list.steps'))

    keys.forEach(key => {
      const stepMatch = /order_confirmation_page.content.step_list.steps.(\d+)/.exec(key)
      if (stepMatch) {
        const stepNumber = stepMatch[1]
        const stepKey = `order_confirmation_page.content.step_list.steps.${stepNumber}`

        if (!steps[stepKey]) {
          steps[stepKey] = {
            id: Number(stepNumber),
            content: '',
            footnote: '',
          }
        }

        if (key.includes('.content')) {
          steps[stepKey].content = messages[key] as string
        } else if (key.includes('.footnote')) {
          steps[stepKey].footnote = messages[key] as string
        }
      }
    })

    return Object.values(steps)
  }, [messages])

  // If the customer data has been cleared, redirect to the 404 page
  if (!records[id]?.paymentIban) {
    return <Navigate to={urls.notFound} />
  }

  return (
    <>
      <Navbar />

      <S.Main>
        <Banner firstName={records[id]?.customerName ?? ''} />

        <S.Page>
          {records[id] ? ( // Only render the page content if the customer data exists / hasnt been cleared
            <>
              <h4>
                <FormattedMessage id="order_confirmation_page.content.step_list.title" />
              </h4>
              <S.Steps>
                {stepList?.map(step => (
                  <S.Step key={step.id}>
                    <S.Circle>
                      <span>{step.id + 1}</span>
                    </S.Circle>
                    <S.StepContent>
                      <ReactMarkdown>{step.content}</ReactMarkdown>
                      {step.id === stepList.length - 1 && (
                        <S.Payee>
                          <p>
                            <FormattedMessage id="quiz_payment_page.content.questions.account_holder.label" />:{' '}
                            {records[id]?.paymentAccountHolder}
                          </p>
                          <p>IBAN: {records[id]?.paymentIban}</p>
                        </S.Payee>
                      )}
                      {step.footnote && <p>{step.footnote}</p>}
                    </S.StepContent>
                  </S.Step>
                ))}
              </S.Steps>
            </>
          ) : null}
        </S.Page>
        <Faq />
      </S.Main>
      <Footer />
    </>
  )
}

export default OrderConfirmation
